import { PropsWithChildren, type FC } from "react";
import { InventoryPriceVariant } from "./ProductQuantitySelector";

export const Sizes: FC<PropsWithChildren> = ({ children }) => (
  <div
    aria-label="Select Size & Quantity"
    className="quantity-selector__sizes relative mb-4 flex w-full flex-wrap gap-y-4 pl-10 md:max-w-[640px] lg:max-w-none"
  >
    {children}
  </div>
);

export const Size: FC<PropsWithChildren> = ({ children }) => {
  return (
    <div className="quantity-selector__size relative flex w-1/5 flex-col before:absolute before:-left-8 before:top-[3.5rem] before:hidden before:text-sm before:font-bold before:text-black before:content-['Qty:'] after:absolute after:-left-[2.25rem] after:top-[5.375rem] after:hidden after:text-[12px] after:text-black after:content-['Stock:'] md:w-1/6 [&:nth-child(5n+1)]:before:block [&:nth-child(5n+1)]:after:block md:[&:nth-child(5n+1)]:before:hidden md:[&:nth-child(5n+1)]:after:hidden md:[&:nth-child(6n+1)]:before:block md:[&:nth-child(6n+1)]:after:block">
      {children}
    </div>
  );
};

export const SizeTop: FC<PropsWithChildren> = ({ children }) => (
  <div className="flex flex-col text-center font-[600]">{children}</div>
);

export const SizeLabel: FC<PropsWithChildren> = ({ children }) => (
  <span
    data-testid="quantitySizeLabel"
    className="size__label text-sm font-[600] text-blue900"
  >
    {children}
  </span>
);

export const SizePrice: FC<PropsWithChildren> = ({ children }) => (
  <span className="size__price leading-0 relative flex h-4 flex-col justify-center font-bold text-green">
    {children}
  </span>
);

export const SizeModifier: FC<
  PropsWithChildren<{ variant: InventoryPriceVariant }>
> = ({ variant }) => {
  const modifier =
    [
      [variant.isCloseout, "Closeout"],
      [variant.isDropship, "Drop Ship"],
      [variant.onSale, "Sale"],
    ]
      .flatMap(([condition, modifier]) => (condition ? modifier : []))
      .join(" / ") || "";

  const className = (() => {
    if (modifier === "Drop Ship") {
      return "text-blue";
    }

    if (modifier === "Closeout / Drop Ship") {
      return "text-black";
    }

    return "text-red200";
  })();

  return (
    <span
      className={`${className} nowrap mb-1 min-h-[13px] text-[9px] font-bold leading-[13px]`}
    >
      {modifier}
    </span>
  );
};

export const SizeField: FC<PropsWithChildren> = ({ children }) => (
  <div className="flex w-full flex-col items-center justify-center gap-1 self-center px-1">
    {children}
  </div>
);

export const SizeFieldInput: FC<{
  size: string;
  max?: number;
  id?: string;
  isLoading?: boolean;
}> = ({ size, max = 0, id, isLoading }) => {
  return (
    <input
      id={`size-input__${size}`}
      name={id}
      type="number"
      placeholder={max === 0 && !isLoading ? "out of stock" : ""}
      disabled={max === 0 || isLoading}
      max={max}
      min={0}
      className={`size-field__input h-[36px] w-full max-w-[60px] rounded border border-gray500 px-2 py-1 text-center outline-blue900 ${max === 0 ? "text-[8px]" : ""} [-moz-appearance:textfield]`}
      aria-label={`Quantity for size ${size}`}
    />
  );
};

export const SizeFieldStock: FC<PropsWithChildren> = ({ children }) => (
  <span className="size-field__stock flex h-4 flex-col justify-center text-center text-[12px] text-gray600">
    {children}
  </span>
);
